import i18n from '@/i18n';
import type { SsoParams, SsoType } from './types';

/** English fallbacks when locale files have not loaded yet (common in embed bundles). */
const HOST_MESSAGE_DEFAULTS: Record<string, string> = {
  'embeddableWidget.host.authenticatingTitle': 'Verifying SSO',
  'embeddableWidget.host.authenticatingMessage':
    'Authenticating with Wealth Access. This may take a moment.',
  'embeddableWidget.host.authRequiredTitle': 'Widget cannot render',
  'embeddableWidget.host.authRequiredMessage':
    'Authentication is required. Call initApi() or initWithToken() first. For multiple widgets, call renderWidget() on each container in the same turn as initApi() (before it resolves) so each container can show SSO progress.',
  'embeddableWidget.host.authFailedTitle': 'Authentication failed',
  'embeddableWidget.host.authFailedMessage':
    'SSO could not complete. Check the token, SSO type, and platform URL, then try again.',
  'embeddableWidget.host.missingToken':
    'A token is required for SSO authentication.',
  'embeddableWidget.host.salesforceMissingEmails':
    'Salesforce SSO requires both advisor and investor email parameters.',
  'embeddableWidget.host.unknownWidgetTitle': 'Widget not found',
  'embeddableWidget.host.unknownWidgetMessage':
    'This widget code is not registered in the bundle. Verify the widget key with your implementation team.',
  'embeddableWidget.host.renderFailedTitle': 'Widget cannot render',
  'embeddableWidget.host.renderFailedMessage':
    'An error occurred while loading this widget.',
  'embeddableWidget.host.loadingDashboardTitle': 'Loading dashboard',
  'embeddableWidget.host.loadingDashboardMessage':
    'Fetching your dashboard configuration.',
  'embeddableWidget.host.emptyDashboardTitle': 'No widgets configured',
  'embeddableWidget.host.emptyDashboardMessage':
    'There are no visible widgets on this dashboard.',
  'embeddableWidget.host.dashboardFetchFailed':
    'Could not load the dashboard (HTTP {{status}}).',
  'embeddableWidget.host.detailLabel': 'Details',
};

export function getWidgetHostMessage(
  key: string,
  options?: Record<string, string>,
): string {
  return i18n.t(key, {
    ...options,
    defaultValue: HOST_MESSAGE_DEFAULTS[key] ?? key,
  });
}

export function formatWidgetHostError(error: unknown): string {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return String(error);
}

function normalizeSsoTypeForValidation(ssoType: SsoType): string {
  return String(ssoType)
    .replace(/^\uFEFF/, '')
    .trim()
    .toLowerCase();
}

/**
 * Validates SSO params before network calls so embedders get a clear message.
 */
export function validateSsoParamsBeforeAuth(
  ssoType: SsoType,
  ssoParams: SsoParams,
): void {
  const normalized = normalizeSsoTypeForValidation(ssoType);
  const token =
    typeof ssoParams.token === 'string' ? ssoParams.token.trim() : '';

  if (!token) {
    throw new Error(getWidgetHostMessage('embeddableWidget.host.missingToken'));
  }

  if (normalized === 'salesforce' || normalized === '_salesforce') {
    const advisor =
      typeof ssoParams.advisor === 'string' ? ssoParams.advisor.trim() : '';
    const investor =
      typeof ssoParams.investor === 'string' ? ssoParams.investor.trim() : '';
    if (!advisor || !investor) {
      throw new Error(
        getWidgetHostMessage('embeddableWidget.host.salesforceMissingEmails'),
      );
    }
  }
}
